import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ArchivoDescargado, archivoDeRespuesta } from '../utils/descarga-archivo';

/** Estados de `api_externa_cliente`. */
export type EstadoClienteApi = 'INVITADO' | 'ACTIVO' | 'DESHABILITADO';

/** Un corredor del registry, tal como lo sirve `GET api-externa-control/productos`. */
export interface ProductoApi {
  id: string;
  origen: string;
  destino: string;
  tipo: string;
  paisDestino: string;
}

/** Un usuario candidato a cliente API (`GET api-externa-control/usuarios?q=`). */
export interface UsuarioCandidatoApi {
  usuarioId: number;
  nombre: string;
  apellidos: string;
  email: string;
  tipoId: string;
  numeroId: string;
  estadoComplianceID: number;
  mailVerificado: number | boolean;
  yaEsClienteApi: boolean;
  /** Estado actual si ya es cliente API (lo manda el backend junto a `yaEsClienteApi`). */
  estadoClienteApi?: EstadoClienteApi | null;
  elegible: boolean;
  causas: string[];
}

/** Espejo de `ClienteApi` del modulo `api-externa` (contrato de la fase 1). */
export interface ClienteApi {
  usuarioId: number;
  nombre: string;
  apellidos: string;
  email: string;
  estado: EstadoClienteApi;
  /** Ids del registry o `["*"]`. */
  productos: string[];
  limitePorMinuto: number | null;
  maxLlaves: number;
  nota: string | null;
  invitadoPor: string;
  invitadoEn: string;
  activadoEn: string | null;
  deshabilitadoEn: string | null;
  deshabilitadoPor: string | null;
  motivo: string | null;
  ultimaVerificacionEn: string | null;
  ultimaVerificacionResultado: string | null;
  /** Fase 2: la llave vigente del cliente (o la ultima), sin clave. `null` si nunca creo una. */
  llave?: LlaveClienteApi | null;
  /** Fase 2: si el cliente ya esta conectado y funcionando. Opcional hasta que el backend lo mande. */
  conexion?: ConexionClienteApi | null;
  /** Límite EFECTIVO (general o personalizado). Si no viene (back viejo) se usa `limitePorMinuto`. */
  limite?: LimiteClienteApi | null;
  /** Fase 3: sesiones vivas de la llave (`api_externa_sesion`). Opcional hasta que el backend lo mande. */
  sesionesActivas?: number | null;
  /** Fase 3: cuando se creo (o uso) la ultima sesion. */
  ultimaSesionEn?: string | null;
  /** Fase 3: atajo del backend; si no viene se deduce de `sesionesActivas > 0`. */
  sesionActiva?: boolean | null;
}

/** `INACTIVA` = tuvo OK hace mas de 24 h y sin errores recientes. */
export type EstadoConexionApi = 'NUNCA' | 'OK' | 'CON_ERRORES' | 'SIN_LLAVE' | 'INACTIVA';

/** Semaforo de conexion de un cliente (`GET api-externa-control/clientes`). */
export interface ConexionClienteApi {
  estado: EstadoConexionApi;
  ultimoOkEn: string | null;
  ultimoFalloEn: string | null;
  ultimoFalloCodigo: string | null;
  ultimoFalloIp: string | null;
  llamadasHoy: number;
  rechazadasHoy: number;
  /** Fase 3: por si el backend cuelga la sesion de la conexion en vez del cliente. */
  sesionesActivas?: number | null;
  ultimaSesionEn?: string | null;
}

/** Interruptor general de la API externa (`GET/PUT api-externa-control/interruptor`). */
export interface InterruptorApi {
  /** `true` = encendida: los clientes con llave pueden consultar. `false` = todos reciben 503. */
  activa: boolean;
  vigilanteActivo: boolean;
  actualizadoEn?: string | null;
  actualizadoPor?: string | null;
}

/** Guia de integracion para operadores (`GET api-externa-control/documentacion`). */
export interface DocumentacionControlApi {
  version: string;
  actualizadoEn: string | null;
  /** Fragmento HTML (sin `<html>`/`<body>` ni scripts). Se muestra con `[innerHTML]`. */
  html: string;
}

/** Tarjetas del tablero (`GET api-externa-control/resumen`). */
export interface ResumenClientesApi {
  habilitados: number;
  activos: number;
  invitados: number;
  deshabilitados: number;
  conLlave: number;
  conectadosHoy: number;
  conErroresHoy: number;
  /** Fase 3: clientes con al menos una sesion viva. Opcional hasta que el backend lo mande. */
  conSesionActiva?: number;
}

/** Lo que el operador ve de una llave (`GET clientes` / `GET clientes/:id`). Nunca la clave. */
export interface LlaveClienteApi {
  /** `api_externa_llave.id`; es lo que va en `POST llaves/:id/revocar`. */
  id?: string;
  kid: string;
  estado: 'ACTIVA' | 'REVOCADA';
  ips: string[];
  nombre?: string | null;
  ultimoUsoEn: string | null;
  ultimoUsoIp?: string | null;
  expiraEn: string | null;
  creadaEn?: string | null;
  revocadaEn?: string | null;
  motivoRevocacion?: string | null;
  /** Rotación con gracia: hasta cuándo sigue firmando la llave revocada (ISO). Opcional. */
  graciaHasta?: string | null;
}

export type TipoEventoApi =
  | 'INVITAR'
  | 'ACTIVAR'
  | 'CONFIGURAR'
  | 'DESHABILITAR'
  | 'REHABILITAR'
  | 'REENVIAR_CORREO'
  | 'VIGILANTE_DESHABILITAR'
  | 'CREAR_LLAVE'
  | 'ROTAR_LLAVE'
  | 'REVOCAR_LLAVE'
  | 'CAMBIAR_IPS';

export interface EventoClienteApi {
  id: number;
  tipo: TipoEventoApi | string;
  actor: string;
  ip: string | null;
  detalle: unknown;
  createdAt: string;
}

export interface ClienteApiDetalle extends ClienteApi {
  eventos: EventoClienteApi[];
  /**
   * Cursor de la página siguiente de eventos (`?antesDeId=`). `null` = no hay
   * más. Opcional: el backend viejo no lo manda (se trata como `null`).
   */
  eventosSiguienteAntesDeId?: number | null;
  /** Si el backend lista también llaves anteriores (p. ej. la revocada en gracia). Opcional. */
  llaves?: LlaveClienteApi[] | null;
  /** Fase 2: uso de la llave (por dia y ruta) si el detalle lo trae. */
  uso?: FilaUsoClienteApi[] | null;
}

/** Fila de `api_externa_uso` vista por el operador. */
export interface FilaUsoClienteApi {
  dia: string;
  ruta: string;
  ok: number;
  http401: number;
  http403: number;
  http429: number;
}

/** Límite efectivo de un cliente: el general del parámetro o uno propio. */
export interface LimiteClienteApi {
  porMinuto: number;
  saldosPorMinuto?: number | null;
  origen: 'general' | 'personalizado';
  /** Valor del límite general vigente. */
  general?: number | null;
}

export interface AltaClienteApi {
  usuarioId: number;
  /** Sin uso desde PX-01 (03/11): el panel ya no manda productos. */
  productos?: string[];
  /** `null` o ausente = límite general. */
  limitePorMinuto?: number | null;
  maxLlaves?: number;
  nota?: string;
}

export interface ConfiguracionClienteApi {
  productos?: string[];
  /** `null` = volver al límite general. */
  limitePorMinuto?: number | null;
  maxLlaves?: number;
  nota?: string;
}

export interface PaginaClientesApi {
  items: ClienteApi[];
  total: number;
}

/**
 * «Clientes API» del panel de operadores: alta por busqueda de usuario,
 * configuracion, deshabilitar, rehabilitar y reenviar el correo.
 *
 * Las llamadas que pueden devolver 409 con `{ message, causas }` (alta y
 * rehabilitacion) saltan el interceptor de errores: ese interceptor aplana
 * cualquier respuesta que no sea 400/422/429 a un string y se perderian las
 * causas, que son justo lo que el operador necesita leer.
 */
@Injectable({ providedIn: 'root' })
export class ApiExternaControlService {
  private baseUrl = `${environment.apiNest}api-externa-control`;

  constructor(private http: HttpClient) {}

  productos(): Promise<ProductoApi[]> {
    return firstValueFrom(this.http.get<ProductoApi[]>(`${this.baseUrl}/productos`));
  }

  buscarUsuarios(q: string): Promise<UsuarioCandidatoApi[]> {
    return firstValueFrom(
      this.http.get<UsuarioCandidatoApi[]>(`${this.baseUrl}/usuarios`, {
        params: new HttpParams().set('q', q.trim()),
      })
    );
  }

  /** `correoEnviado` es `false` si el alta quedó hecha pero SES falló: el operador reenvía. */
  crear(alta: AltaClienteApi): Promise<ClienteApi & { correoEnviado?: boolean }> {
    return firstValueFrom(
      this.http.post<ClienteApi & { correoEnviado?: boolean }>(`${this.baseUrl}/clientes`, alta, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  listar(filtro: {
    estado?: EstadoClienteApi | '';
    q?: string;
    page: number;
    limit: number;
  }): Promise<PaginaClientesApi> {
    let params = new HttpParams().set('page', filtro.page).set('limit', filtro.limit);
    if (filtro.estado) params = params.set('estado', filtro.estado);
    // El backend ignora `q` de menos de 2 caracteres: no se manda para no filtrar en silencio.
    if (filtro.q?.trim() && filtro.q.trim().length >= 2) params = params.set('q', filtro.q.trim());
    return firstValueFrom(
      this.http.get<PaginaClientesApi>(`${this.baseUrl}/clientes`, { params })
    );
  }

  /** La misma guia que ve el cliente, para que el operador la lea sin salir del panel. */
  documentacion(): Promise<DocumentacionControlApi> {
    return firstValueFrom(
      this.http.get<DocumentacionControlApi>(`${this.baseUrl}/documentacion`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /** El OpenAPI como archivo (`GET documentacion/openapi`); 404 = no disponible. */
  openapi(): Promise<Blob> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/documentacion/openapi`, {
        responseType: 'blob',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /** Estado del interruptor. Salta el interceptor: si no existe aun, el bloque muestra «—». */
  interruptor(): Promise<InterruptorApi> {
    return firstValueFrom(
      this.http.get<InterruptorApi>(`${this.baseUrl}/interruptor`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /** Enciende o apaga la API para todos. El motivo (>= 3) queda en la bitacora. */
  cambiarInterruptor(activa: boolean, motivo: string): Promise<InterruptorApi> {
    return firstValueFrom(
      this.http.put<InterruptorApi>(
        `${this.baseUrl}/interruptor`,
        { activa, motivo: motivo.trim() },
        { headers: { 'X-Skip-Error-Interceptor': 'true' } }
      )
    );
  }

  /**
   * Tarjetas del tablero. Salta el interceptor: si el backend todavia no
   * expone `/resumen`, el tablero muestra «—» en vez de un modal de error.
   */
  resumen(): Promise<ResumenClientesApi> {
    return firstValueFrom(
      this.http.get<ResumenClientesApi>(`${this.baseUrl}/resumen`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /**
   * Uso de la llave de un cliente por dia y ruta (`GET clientes/:id/uso?dias=`).
   * Ruta fuera del contrato §12.4: se tolera que no exista (el detalle avisa).
   */
  async usoCliente(usuarioId: number, dias = 7): Promise<FilaUsoClienteApi[]> {
    const r = await firstValueFrom(
      this.http.get<unknown>(`${this.baseUrl}/clientes/${usuarioId}/uso`, {
        params: new HttpParams().set('dias', dias),
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
    const lista = Array.isArray(r)
      ? r
      : (r as { uso?: unknown })?.uso ?? (r as { items?: unknown })?.items ?? [];
    if (!Array.isArray(lista)) return [];
    return lista.map((f) => {
      const x = (f ?? {}) as Record<string, unknown>;
      return {
        dia: String(x['dia'] ?? ''),
        ruta: String(x['ruta'] ?? ''),
        ok: Number(x['ok'] ?? 0),
        http401: Number(x['http401'] ?? 0),
        http403: Number(x['http403'] ?? 0),
        http429: Number(x['http429'] ?? 0),
      };
    });
  }

  /**
   * Detalle del cliente. Sin `pagina` es la carga de siempre; con
   * `antesDeId` pide la página siguiente de eventos (`?antesDeId=&limite=`).
   */
  detalle(usuarioId: number, pagina?: { antesDeId: number; limite?: number }): Promise<ClienteApiDetalle> {
    let params = new HttpParams();
    if (pagina) {
      params = params.set('antesDeId', pagina.antesDeId);
      if (pagina.limite) params = params.set('limite', pagina.limite);
    }
    return firstValueFrom(
      this.http.get<ClienteApiDetalle>(`${this.baseUrl}/clientes/${usuarioId}`, { params })
    );
  }

  configurar(usuarioId: number, cambios: ConfiguracionClienteApi): Promise<ClienteApi> {
    return firstValueFrom(
      this.http.put<ClienteApi>(`${this.baseUrl}/clientes/${usuarioId}`, cambios)
    );
  }

  deshabilitar(usuarioId: number, motivo: string): Promise<ClienteApi> {
    return firstValueFrom(
      this.http.post<ClienteApi>(`${this.baseUrl}/clientes/${usuarioId}/deshabilitar`, {
        motivo,
      })
    );
  }

  rehabilitar(usuarioId: number, nota?: string): Promise<ClienteApi> {
    const body = nota?.trim() ? { nota: nota.trim() } : {};
    return firstValueFrom(
      this.http.post<ClienteApi>(`${this.baseUrl}/clientes/${usuarioId}/rehabilitar`, body, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  reenviarCorreo(usuarioId: number): Promise<{ enviado: boolean }> {
    return firstValueFrom(
      this.http.post<{ enviado: boolean }>(
        `${this.baseUrl}/clientes/${usuarioId}/reenviar-correo`,
        {}
      )
    );
  }

  /**
   * Fase 2: revoca de emergencia la llave de un cliente. `llaveId` es
   * `api_externa_llave.id`. El motivo (>= 3) lo ve el cliente. Salta el
   * interceptor porque el 409 (ya revocada) trae `codigo` que se muestra.
   */
  revocarLlave(llaveId: string, motivo: string): Promise<LlaveClienteApi | ClienteApi> {
    return firstValueFrom(
      this.http.post<LlaveClienteApi | ClienteApi>(
        `${this.baseUrl}/llaves/${encodeURIComponent(llaveId)}/revocar`,
        { motivo: motivo.trim() },
        { headers: { 'X-Skip-Error-Interceptor': 'true' } }
      )
    );
  }

  // ================================================================ fase 3: documentación

  /** OpenAPI del cliente como texto, para el visor Swagger. 404 = no disponible. */
  openapiTexto(): Promise<string> {
    return this.texto('documentacion/openapi');
  }

  /** OpenAPI de la API de operadores (`documentacion/operadores/openapi`) como texto. */
  openapiOperadoresTexto(): Promise<string> {
    return this.texto('documentacion/operadores/openapi');
  }

  /** OpenAPI del cliente como archivo, con el nombre de `Content-Disposition`. */
  openapiArchivo(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/openapi', 'aguacate-integraciones-v1.openapi.yaml');
  }

  /** Colección Postman del cliente (`GET documentacion/postman`). */
  postman(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/postman', 'aguacate-integraciones-v1.postman_collection.json');
  }

  /** Entorno Postman del cliente (`GET documentacion/postman-entorno/qa|prod`). */
  postmanEntorno(entorno: 'qa' | 'prod'): Promise<ArchivoDescargado> {
    return this.descargar(
      `documentacion/postman-entorno/${entorno}`,
      `aguacate-integraciones-v1.postman_environment.${entorno}.json`
    );
  }

  /**
   * Entorno Postman del ambiente que responde (`GET documentacion/postman-entorno`,
   * sin parametro). `postmanEntorno(qa|prod)` queda sin uso desde el 03/11.
   */
  postmanEntornoActual(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/postman-entorno', 'aguacate-integraciones-v1.postman_environment.json');
  }

  /** OpenAPI de la API de operadores como archivo (solo operador). */
  openapiOperadores(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/operadores/openapi', 'aguacate-operadores.openapi.yaml');
  }

  /** Entorno Postman de operadores (`GET documentacion/operadores/postman-entorno`). */
  postmanEntornoOperadores(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/operadores/postman-entorno', 'aguacate-operadores.postman_environment.json');
  }

  /** Colección Postman de operadores (`GET documentacion/operadores/postman`). */
  postmanOperadores(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/operadores/postman', 'aguacate-operadores.postman_collection.json');
  }

  private texto(ruta: string): Promise<string> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/${ruta}`, {
        responseType: 'text',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /**
   * Baja un archivo con el token de siempre y lee el nombre de
   * `Content-Disposition` (expuesto por CORS). Salta el interceptor: 404 =
   * archivo todavía no publicado, que la pantalla muestra en su lugar.
   */
  private async descargar(ruta: string, nombrePorDefecto: string): Promise<ArchivoDescargado> {
    const r = await firstValueFrom(
      this.http.get(`${this.baseUrl}/${ruta}`, {
        responseType: 'blob',
        observe: 'response',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
    return archivoDeRespuesta(r, nombrePorDefecto);
  }
}
